import type { ExportJobView } from "@planerius/contracts/cabinet"
import type { ReactNode } from "react"
import { useEffect, useState } from "react"

import { clientsExportAvailable, requestClientsExport } from "../../api/clients.api"
import { isApiProblem } from "../../api/problem"
import { Button } from "../../ui"
import styles from "./suspended-screen.module.css"
import {
  buildSuspendedView,
  SUSPENDED_EXPORT_PURPOSE,
  suspendedStillWorks,
  type TenantAccessStatus,
} from "./suspended-model"
import type { TenantRoleCode } from "@planerius/contracts/cabinet-auth"

/**
 * 4-Г4 «Доступ приостановлен» — экран вместо кабинета, а не полоса над ним.
 *
 * ПОЧЕМУ ЭКРАН, А НЕ ПОЛОСА. Полоса сообщает, что работать ещё можно, — а здесь уже
 * нельзя: сервер отказывает каждому запросу. Кабинет с полосой и нерабочими кнопками
 * человек читает как сломанный и звонит нам; экран объясняет и называет, что делать.
 *
 * ГЛАВНОЕ НА ЭКРАНЕ — НЕ КНОПКА ОПЛАТЫ, А СПИСОК «ЧТО ПО-ПРЕЖНЕМУ РАБОТАЕТ». Человек,
 * увидевший закрытый кабинет, первым делом думает, что потерял базу клиентов. Пока
 * ему не сказали обратное, он звонит; сказали — не звонит.
 *
 * ВЫГРУЗКА БАЗЫ ОТСЮДА РАЗРЕШЕНА. Клиентская база принадлежит салону, и удерживать её
 * до оплаты значит торговать тем, что нам не принадлежит. Кнопка здесь потому, что
 * остальной кабинет закрыт: попасть в раздел клиентов человек уже не может.
 *
 * НО «РАЗРЕШЕНА» И «РАБОТАЕТ» — РАЗНЫЕ ВЕЩИ, и экран обязан их различать. Обработчик
 * выгрузок пока не подключён ни в одном окружении, поэтому список «что работает»
 * спрашивает сервер и говорит правду: база ваша, файл мы пока не формируем. Обещание,
 * которого мы не держим, читается как «базу не отдают за неуплату» — то есть наносит
 * ровно тот вред, против которого этот экран написан.
 */
export function SuspendedScreen(props: {
  readonly status: TenantAccessStatus
  readonly tenantName: string
  readonly roleCode: TenantRoleCode | null
  readonly canExport: boolean
  /**
   * Уйти в раздел оплаты и в раздел поддержки.
   *
   * ОБА ВЕДУТ ВНУТРЬ КАБИНЕТА, А НЕ НА ПОЧТУ. Адреса поддержки в продукте ещё нет
   * (решение владельца, вопрос 13), и подставить сюда выдуманный значит отправить
   * человека писать в пустоту. Разделы существуют оба и честно говорят о своём
   * состоянии, а шлюз ограничения их пропускает — иначе кнопка вела бы в этот же экран.
   */
  readonly onPay: () => void
  readonly onSupport: () => void
  /**
   * Выйти и открыть список своих салонов.
   *
   * БЕЗ НИХ ЭКРАН — ЛОВУШКА. Оболочки здесь нет по замыслу (кабинет с нерабочими
   * кнопками читается как сломанный), но вместе с ней пропали ЕДИНСТВЕННЫЕ выходы:
   * человек с двумя салонами не мог перейти во второй, где он работает, а мастер
   * приостановленного салона не мог даже выйти из учётной записи — при том, что сервер
   * оба действия разрешает (@AllowTenantless).
   *
   * «Мои салоны», а не «Другой салон»: сколько их у человека, оболочка не знает (в /auth/me
   * членств нет), и обещать второй салон тому, у кого он один, — обман. Список салонов
   * честен при любом их числе, там же лежат и приглашения.
   */
  readonly onSignOut: () => void
  readonly onMyTenants: () => void
}): ReactNode {
  const [job, setJob] = useState<ExportJobView | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Пока сервер не ответил, считаем, что файлы формируются: список «что работает»
  // не должен мигать с «работает» на «не работает» на глазах у человека.
  const [exportAvailable, setExportAvailable] = useState(true)

  useEffect(() => {
    const controller = new AbortController()
    void clientsExportAvailable(controller.signal)
      .then((result) => {
        setExportAvailable(result.available)
      })
      .catch(() => {
        // Не ответил — не повод пугать: обещание останется прежним, а нажатие
        // объяснит причину словами сервера.
      })
    return () => {
      controller.abort()
    }
  }, [])

  // Вид собирается ПОСЛЕ ответа сервера о доступности выгрузки: главный абзац экрана
  // не должен обещать файл, которого не будет.
  const view = buildSuspendedView({
    status: props.status,
    roleCode: props.roleCode,
    canExport: props.canExport,
    exportAvailable,
    tenantName: props.tenantName,
  })

  const exportBase = (): void => {
    setBusy(true)
    setNote(null)
    void (async () => {
      try {
        setJob(
          await requestClientsExport({
            format: "csv",
            purpose: SUSPENDED_EXPORT_PURPOSE,
          }),
        )
      } catch (error) {
        /*
         * ПРИЧИНА НАЗЫВАЕТСЯ СЛОВАМИ СЕРВЕРА. У отказа выгрузки есть подробное объяснение
         * (problem.detail): «файлы пока не формируются, запрос не принят, квота
         * не израсходована». Раньше здесь читалось error.message — у нашего класса
         * ошибки это «код: заголовок», то есть человек видел «export_unavailable:
         * Выгрузка данных сейчас недоступна» и шёл в поддержку за ответом, который
         * сервер уже дал.
         */
        setNote(
          isApiProblem(error)
            ? (error.detail ?? error.title ?? "Выгрузка не запустилась")
            : "Выгрузка не запустилась",
        )
      } finally {
        setBusy(false)
      }
    })()
  }

  return (
    <div className={styles["screen"]}>
      <section className={styles["card"]}>
        <h1 className={styles["title"]}>{view.title}</h1>
        <p className={styles["text"]}>{view.text}</p>

        <ul className={styles["list"]}>
          {suspendedStillWorks({ exportAvailable }).map((line) => (
            <li key={line} className={styles["item"]}>
              {line}
            </li>
          ))}
        </ul>

        <div className={styles["actions"]}>
          {view.action === null ? null : (
            <Button
              variant={view.action.kind === "pay" ? "primary" : "secondary"}
              onClick={view.action.kind === "pay" ? props.onPay : props.onSupport}
            >
              {view.action.label}
            </Button>
          )}

          {view.canExport ? (
            <Button variant="secondary" disabled={busy} onClick={exportBase}>
              Выгрузить базу клиентов
            </Button>
          ) : null}
        </div>

        {/*
          * ВЫХОД И ПЕРЕХОД В ДРУГОЙ САЛОН — НЕ УКРАШЕНИЕ, А ЕДИНСТВЕННЫЕ ДВЕРИ.
          * Оболочки с навигацией здесь нет, и без этих двух кнопок человек запирался
          * в экране, который сам ничего не решает: мастер не мог выйти, владелец сети —
          * перейти в работающий салон.
          */}
        <div className={styles["actions"]}>
          <Button variant="ghost" onClick={props.onMyTenants}>
            Мои салоны
          </Button>
          <Button variant="ghost" onClick={props.onSignOut}>
            Выйти
          </Button>
        </div>

        {job === null ? null : (
          <div className={styles["note"]} role="status">
            {job.status === "ready" && job.downloadUrl !== null ? (
              <>
                Файл готов.{" "}
                <a href={job.downloadUrl} className={styles["support"]}>
                  Скачать
                </a>
                {job.expiresAt === null ? null : " — ссылка живёт ограниченное время."}
              </>
            ) : (
              // Асинхронность названа вслух: без этого человек ждёт файл в браузере
              // и уходит с экрана, решив, что кнопка не сработала.
              "Готовим файл. Это занимает время: когда он будет готов, ссылка появится здесь, а задание не пропадёт, если обновить страницу."
            )}
          </div>
        )}

        {note === null ? null : (
          <div className={styles["note"]} role="alert">
            {note}
          </div>
        )}
      </section>
    </div>
  )
}
